import {
  BitkitIconButton,
  BitkitIconButtonProps,
  BitkitTextInput,
  BitkitTextInputProps,
  IconEye,
  IconEyeSlash,
  useResponsive,
} from '@bitrise/bitkit-v2';
import { Box } from '@chakra-ui/react/box';
import { Popover } from '@chakra-ui/react/popover';
import { Portal } from '@chakra-ui/react/portal';
import { ReactNode, RefObject, useEffect, useId, useMemo, useRef, useState } from 'react';

import ValidationTooltip from '@/components/ValidationTooltip/ValidationTooltip';
import { ValidationResultSummary } from '@/models/Validation';
import {
  SimpleValidationKeys,
  StandardValidationKeys,
  validatePasswordWithoutEmailAndUsername,
} from '@/utils/formValidators/passwordValidator';

type RequirementsPopoverProps = {
  anchorRef: RefObject<HTMLDivElement | null>;
  children: ReactNode;
  open: boolean;
  title: string;
};

// Opens with the input's focus and closes with its blur, so it has no close button and never takes focus.
const RequirementsPopover = ({ anchorRef, children, open, title }: RequirementsPopoverProps) => (
  <Popover.Root
    // eslint-disable-next-line jsx-a11y/no-autofocus
    autoFocus={false}
    open={open}
    positioning={{ getAnchorElement: () => anchorRef.current, placement: 'right' }}
  >
    <Portal>
      <Popover.Positioner>
        <Popover.Content>
          <Popover.Header>
            <Popover.Title>{title}</Popover.Title>
          </Popover.Header>
          <Popover.Body>{children}</Popover.Body>
        </Popover.Content>
      </Popover.Positioner>
    </Portal>
  </Popover.Root>
);

type PasswordInputAndIconProps = BitkitTextInputProps & {
  showText?: string;
  hideText?: string;
  iconButtonSize?: BitkitIconButtonProps['size'];
};

const PasswordInputAndIcon = ({
  inputProps,
  onChange,
  showText = 'Show password',
  hideText = 'Hide password',
  iconButtonSize = 'sm',
  ...rest
}: PasswordInputAndIconProps) => {
  const [revealPassword, setRevealPassword] = useState(false);

  return (
    <BitkitTextInput
      endElement={
        <BitkitIconButton
          label={revealPassword ? hideText : showText}
          icon={revealPassword ? IconEyeSlash : IconEye}
          onClick={() => setRevealPassword(!revealPassword)}
          variant="tertiary"
          size={iconButtonSize}
        />
      }
      inputProps={{
        ...inputProps,
        onChange: (event) => {
          inputProps?.onChange?.(event);
          onChange?.(event);
        },
        type: revealPassword ? 'text' : 'password',
      }}
      {...rest}
    />
  );
};

export type RevealablePasswordInputProps = PasswordInputAndIconProps;

export const RevealablePasswordInput = (props: RevealablePasswordInputProps) => <PasswordInputAndIcon {...props} />;

export type ValidatedRevealablePasswordInputProps = RevealablePasswordInputProps & {
  onValidate?: (isValid: boolean) => void;
  email?: string;
  username?: string;
  login?: string;
};

export const ValidatedRevealablePasswordInput = ({
  value = '',
  email: _email,
  username: _username,
  login: _login,
  inputProps,
  onValidate,
  ...rest
}: ValidatedRevealablePasswordInputProps) => {
  const { isMobile } = useResponsive();
  const [password, setPassword] = useState(value);
  const [hasFocus, setHasFocus] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const requirementsId = useId();

  const validationResult = useMemo(() => validatePasswordWithoutEmailAndUsername(`${password}`), [password]);

  useEffect(() => {
    if (onValidate) {
      onValidate(validationResult.isValid);
    }
  }, [onValidate, validationResult.isValid]);

  useEffect(() => {
    setShowTooltip(isMobile || hasFocus || (!!password && !validationResult.isValid));
  }, [isMobile, hasFocus, password, validationResult.isValid]);

  const inputAndIcon = (
    <PasswordInputAndIcon
      value={password}
      inputProps={{
        ...inputProps,
        'aria-describedby': requirementsId,
        onBlur: (event) => {
          setHasFocus(false);
          inputProps?.onBlur?.(event);
        },
        onChange: (event) => {
          setPassword(event.target.value);
          inputProps?.onChange?.(event);
        },
        onFocus: (event) => {
          setHasFocus(true);
          inputProps?.onFocus?.(event);
        },
      }}
      {...rest}
    />
  );

  const requirements = <ValidationTooltip id={requirementsId} results={validationResult.results} />;

  return (
    <Box display="flex" flexDirection="column" gap="12">
      {isMobile && (
        <>
          {inputAndIcon}
          {showTooltip && (
            <Box display="flex" flexDirection="column" gap="8">
              <ValidationTooltip id={requirementsId} results={validationResult.results} title="Password requirements" />
            </Box>
          )}
        </>
      )}
      {!isMobile && (
        <>
          <Box ref={anchorRef}>{inputAndIcon}</Box>
          <RequirementsPopover anchorRef={anchorRef} open={hasFocus} title="Password requirements">
            {requirements}
          </RequirementsPopover>
        </>
      )}
    </Box>
  );
};

export type RevealablePasswordInputWithValidationProps = ValidatedRevealablePasswordInputProps & {
  validationResults: ValidationResultSummary<SimpleValidationKeys | StandardValidationKeys>;
};

export const RevealablePasswordInputWithValidation = ({
  value = '',
  inputProps,
  onValidate = () => {},
  validationResults,
  ...rest
}: RevealablePasswordInputWithValidationProps) => {
  const { isMobile } = useResponsive();
  const [password, setPassword] = useState(value);
  const [hasFocus, setHasFocus] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const requirementsId = useId();

  useEffect(() => {
    if (onValidate) {
      onValidate(validationResults.isValid);
    }
  }, [onValidate, validationResults.isValid]);

  useEffect(() => {
    setShowTooltip(isMobile || hasFocus || (!!password && !validationResults.isValid));
  }, [isMobile, hasFocus, password, validationResults.isValid]);

  const inputAndIcon = (
    <PasswordInputAndIcon
      value={password}
      inputProps={{
        ...inputProps,
        'aria-describedby': requirementsId,
        onBlur: (event) => {
          setHasFocus(false);
          inputProps?.onBlur?.(event);
        },
        onChange: (event) => {
          setPassword(event.target.value);
          inputProps?.onChange?.(event);
        },
        onFocus: (event) => {
          setHasFocus(true);
          inputProps?.onFocus?.(event);
        },
      }}
      {...rest}
    />
  );

  const requirements = <ValidationTooltip id={requirementsId} results={validationResults.results} />;

  return (
    <Box display="flex" flexDirection="column">
      {isMobile && (
        <>
          {inputAndIcon}
          {showTooltip && (
            <Box display="flex" flexDirection="column" gap="8" marginTop="24px">
              <ValidationTooltip
                id={requirementsId}
                results={validationResults.results}
                title="Your password should have:"
              />
            </Box>
          )}
        </>
      )}
      {!isMobile && (
        <>
          <Box ref={anchorRef}>{inputAndIcon}</Box>
          <RequirementsPopover anchorRef={anchorRef} open={hasFocus} title="Your password should have:">
            {requirements}
          </RequirementsPopover>
        </>
      )}
    </Box>
  );
};
